/* ygh-3d.js - shared 3D model viewer for YGH (Google <model-viewer> 4.1).
   Upgrades every [data-ygh3d] element on the page, now and later (MutationObserver), so a
   section, a drawer, a gallery slide or a corner tile all share one loader and one look:
   the model, a slow spin, and one hint pill (drag to rotate, scroll or pinch to zoom). No buttons.

   Attributes on the element (all optional except src):
     data-ygh3d-src       .glb URL (meshopt-compressed files are fine; decoder is wired below)
     data-ygh3d-usdz      .usdz for iOS Quick Look (AR)
     data-ygh3d-poster    image shown until the model is in; white backgrounds multiply away
     data-ygh3d-sky       .hdr environment image
     data-ygh3d-mode      auto (load when scrolled near / shown) | click (tap the poster first)
     data-ygh3d-lazy      strict = load only when really inside the viewport (drawers)
     data-ygh3d-rotate    1|0 auto-rotate (default 1)  data-ygh3d-speed  e.g. 14deg
     data-ygh3d-orbit     starting camera orbit (default "35deg 72deg auto")
     data-ygh3d-hint      1 (default, device-aware text) | 0 | your own text
     data-ygh3d-ar        1 = "See it in your yard" pill on phones that support AR
     data-ygh3d-interact  0 = display only (no drag, clicks pass through)
     data-ygh3d-label     button text in click mode     data-ygh3d-title  name used in full screen
     data-ygh3d-key       tracking key (yghTrack '3d_model', '<key>|load|interact|expand|ar')

   Full-screen viewer: window.ygh3d.openFull(cfg). Spec-drawer views: [data-ygh3d-tab="3d|0|1.."]
   + [data-ygh3d-tabfor="<plan gallery id>"]. Corner tile [data-ygh3d-corner]: a tap dispatches
   'ygh3d:corner' (bubbles, detail = cfg) and the hero gallery fills its stage with the model. */
(function () {
  'use strict';
  if (window.ygh3d) return;

  // Android / desktop: newest player (Rai, 29 Oct: AR good on the 17T). iPhone / iPad: 4.1.0, the version that opens
  // Quick Look straight away (4.4.0 opened the .usdz as a page there, one extra tap). Quick Look is Apple's own viewer,
  // so the newer player adds nothing on iOS. ?mv=4.x.y on any page URL overrides, to compare on a phone.
  var IS_IOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  var AR_OK = IS_IOS;   // AR on iPhone / iPad only for now (Rai 10-30: Android AR is inconsistent on some phones); Android gets the viewer + Full screen like desktop
  var MV_VER = (function () { var m = /[?&]mv=(4\.\d{1,2}\.\d{1,2})(?:&|#|$)/.exec(location.search); return m ? m[1] : (IS_IOS ? '4.1.0' : '4.4.0'); })();
  var LIB = 'https://cdn.jsdelivr.net/npm/@google/model-viewer@' + MV_VER + '/dist/model-viewer.min.js';
  // the live WebXR session, so Start over / size switch can end it and open AR again (as on yourgreenhouses.design/ar)
  var xrSession = null;
  if (navigator.xr && navigator.xr.requestSession && !navigator.xr.__ygh3d) {
    var rsOrig = navigator.xr.requestSession.bind(navigator.xr);
    navigator.xr.requestSession = function () { return rsOrig.apply(null, arguments).then(function (s) { xrSession = s; s.addEventListener('end', function () { if (xrSession === s) xrSession = null; }); return s; }); };
    navigator.xr.__ygh3d = 1;
  }
  var MESHOPT = 'https://cdn.jsdelivr.net/npm/meshoptimizer@0.22.0/meshopt_decoder.js';
  var libAsked = false, waiters = [];
  var COARSE = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
  var HINT_DEFAULT = COARSE ? 'Drag to rotate · Pinch to zoom' : 'Drag to rotate · Scroll to zoom';

  function libReady() { return !!(window.customElements && customElements.get('model-viewer')); }
  function flush() { var w = waiters; waiters = []; w.forEach(function (f) { try { f(); } catch (e) {} }); }
  function loadLib(cb) {
    if (cb) waiters.push(cb);
    if (libReady()) { flush(); return; }
    if (libAsked) return;
    libAsked = true;
    self.ModelViewerElement = self.ModelViewerElement || {};
    if (!self.ModelViewerElement.meshoptDecoderLocation) self.ModelViewerElement.meshoptDecoderLocation = MESHOPT;
    var s = document.createElement('script');
    s.type = 'module';
    s.src = LIB;
    document.head.appendChild(s);
    if (customElements.whenDefined) customElements.whenDefined('model-viewer').then(flush);
  }
  function track(key, action, extra) {
    try { if (window.yghTrack) window.yghTrack('3d_model', (key || 'viewer') + '|' + action, 0, extra || {}); } catch (e) {}
  }
  // time with the model (Rai 11-02): seconds the loaded viewer was at least half on screen with the tab open (val), and seconds of turning it
  // (extra.active: user camera moves less than 2 s apart). One 3d_model "<key>|time" event per report, the change since the last one, added
  // right before the tracker's last send of the page or a hidden tab (window.yghTrackBeforeSend in ygh-track.js)
  var TIMES = [];
  function watchTime(st) {
    if (st.tm || !st.mv) return;
    var tm = st.tm = { on: 0, since: 0, act: 0, lastMove: 0, sent: 0, sentAct: 0 }, vis = function () { return document.visibilityState !== 'hidden'; };
    TIMES.push(st);
    if (window.IntersectionObserver) new IntersectionObserver(function (es) { es.forEach(function (x) { var inView = x.isIntersecting && x.intersectionRatio >= 0.5; if (inView && !tm.since && vis()) tm.since = Date.now(); else if (!inView && tm.since) { tm.on += Date.now() - tm.since; tm.since = 0; } }); }, { threshold: [0, 0.5] }).observe(st.el);
    document.addEventListener('visibilitychange', function () { if (!vis() && tm.since) { tm.on += Date.now() - tm.since; tm.since = 0; } });
    st.mv.addEventListener('camera-change', function (ev) { if (!ev.detail || ev.detail.source !== 'user-interaction') return; var n = Date.now(); if (tm.lastMove && n - tm.lastMove < 2000) tm.act += n - tm.lastMove; tm.lastMove = n; });
  }
  function reportTimes() {
    TIMES.forEach(function (st) {
      var tm = st.tm; if (tm.since) { tm.on += Date.now() - tm.since; tm.since = document.visibilityState === 'hidden' ? 0 : Date.now(); }
      var on = Math.round(tm.on / 1000), act = Math.round(tm.act / 1000);
      if (on - tm.sent < 1) return;
      try { if (window.yghTrack) window.yghTrack('3d_model', (st.cfg.key || 'viewer') + '|time', Math.min(on - tm.sent, 1800), { active: Math.max(0, act - tm.sentAct) }); } catch (e) {}
      tm.sent = on; tm.sentAct = act;
    });
  }
  (function hook(n) { if (window.yghTrackBeforeSend) window.yghTrackBeforeSend(reportTimes); else if (n < 40) setTimeout(function () { hook(n + 1); }, 500); })(0);
  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }

  var SVG = function (d, extra) { return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"' + (extra || '') + '>' + d + '</svg>'; };
  var ICON = {
    cube: SVG('<path d="M12 2l8 4.5v11L12 22l-8-4.5v-11z"/><path d="M12 22V11.5M4 6.5l8 5 8-5"/>', ' stroke-width="1.8"'),
    close: SVG('<path d="M6 6l12 12M18 6L6 18"/>'),
    ar: SVG('<rect x="6" y="2" width="12" height="20" rx="2"/><path d="M12 8l3 1.7v3.6L12 15l-3-1.7V9.7z"/><path d="M12 15v-3.6M9 9.7l3 1.7 3-1.7"/>'),
    hand: SVG('<path d="M9 11.5V5.5a1.5 1.5 0 0 1 3 0v5"/><path d="M12 10.5a1.5 1.5 0 0 1 3 0v1.5"/><path d="M15 11.5a1.5 1.5 0 0 1 3 0v1.5"/><path d="M18 12.5a1.5 1.5 0 0 1 3 0V16a6 6 0 0 1-6 6h-2.2a6 6 0 0 1-5-2.7L5 15.3a1.5 1.5 0 0 1 2.5-1.6L9 15.5"/>', ' stroke-width="2.2"')
  };

  function cfgOf(el) {
    var g = function (n, d) { var v = el.getAttribute('data-ygh3d-' + n); return v === null ? d : v; };
    var hint = g('hint', '1');
    return {
      src: g('src', ''), usdz: g('usdz', ''), poster: g('poster', ''), sky: g('sky', ''),
      mode: g('mode', 'auto'), lazy: g('lazy', ''), rotate: g('rotate', '1') !== '0', speed: g('speed', '14deg'),
      orbit: (window.matchMedia && window.matchMedia('(max-width: 768px)').matches && g('orbit-m', '')) || g('orbit', '35deg 72deg auto'), ar: AR_OK && g('ar', '0') === '1', interact: g('interact', '1') !== '0',
      hint: hint === '0' ? '' : (hint === '1' ? HINT_DEFAULT : hint),
      label: g('label', 'See it in 3D'), title: g('title', '3D model'), key: g('key', 'viewer'),
      delay: parseInt(g('delay', '0'), 10) || 0,
      // second size with its own model (Arrow 8'): data-ygh3d-alt-match = start of the size name, plus its files
      altMatch: g('alt-match', ''), altSrc: g('alt-src', ''), altUsdz: g('alt-usdz', '')
    };
  }

  // ---- one stage ----
  function mount(el) {
    if (!el || el.__ygh3d) return el ? el.__ygh3d : null;
    var cfg = cfgOf(el);
    if (!cfg.src) return null;
    cfg.mainSrc = cfg.src; cfg.mainUsdz = cfg.usdz;
    if (cfg.altMatch && cfg.altSrc && useAltSize(cfg, window.__npwmpSel)) { cfg.src = cfg.altSrc; cfg.usdz = cfg.altUsdz; }
    el.classList.add('ygh3d');
    if (!cfg.interact) el.classList.add('ygh3d--display');
    var html = '<div class="ygh3d__poster">' +
      (cfg.poster ? '<img src="' + esc(cfg.poster) + '" alt="" decoding="async">' : '') +
      (cfg.mode === 'click'
        ? '<button type="button" class="ygh3d__launch">' + ICON.cube + '<span>' + esc(cfg.label) + '</span></button>'
        : '<span class="ygh3d__spin" aria-hidden="true"></span>') +
      '</div>';
    html += '<span class="ygh3d__progress" aria-hidden="true"><i></i></span>';
    if (cfg.hint && cfg.interact) html += '<span class="ygh3d__hint">' + ICON.hand + '<span>' + esc(cfg.hint) + '</span></span>';
    if (cfg.ar && cfg.interact) html += '<button type="button" class="ygh3d__ar" data-ygh3d-act="ar" hidden>' + ICON.ar + '<span>See it in your yard</span></button>';
    el.innerHTML = html;
    var st = { el: el, cfg: cfg, mv: null, started: false };
    el.__ygh3d = st;

    el.addEventListener('click', function (e) {
      var b = e.target.closest && e.target.closest('[data-ygh3d-act]');
      if (b && el.contains(b)) { e.preventDefault(); e.stopPropagation(); act(st, b.getAttribute('data-ygh3d-act')); return; }
      if (!st.started && cfg.mode === 'click') { e.preventDefault(); e.stopPropagation(); track(cfg.key, 'open'); start(st); }
    });
    // Wheel zoom inside the stage must not scroll the page away from under the pointer.
    el.addEventListener('wheel', function (e) { if (st.mv && cfg.interact) e.preventDefault(); }, { passive: false });
    observe(st);
    return st;
  }

  function observe(st) {
    var cfg = st.cfg, el = st.el;
    if (!('IntersectionObserver' in window)) { loadLib(); if (cfg.mode === 'auto') start(st); return; }
    var strict = cfg.lazy === 'strict';
    // strict (drawers): still shown and a quarter on screen 0.6 s later, so the moment the drawer markup sits in the page while it boots
    // does not count; a later opening fires the observer again
    function settle() {
      clearTimeout(st.wt);
      st.wt = setTimeout(function () {
        if (st.started || !shown(el)) return;
        var r = el.getBoundingClientRect(), vh = window.innerHeight || 1, vw = window.innerWidth || 1;
        var vis = Math.max(0, Math.min(r.bottom, vh) - Math.max(r.top, 0)) * Math.max(0, Math.min(r.right, vw) - Math.max(r.left, 0));
        if (!r.width || !r.height || vis / (r.width * r.height) < 0.25) return;
        loadLib();
        if (cfg.mode === 'auto') start(st);
      }, 600);
    }
    var io = new IntersectionObserver(function (en) {
      for (var i = 0; i < en.length; i++) {
        if (!en[i].isIntersecting) continue;
        if (strict) { settle(); return; }
        loadLib();
        if (cfg.mode === 'auto') start(st);
        io.disconnect();
        return;
      }
    }, { rootMargin: strict ? '0px' : '240px', threshold: strict ? [0.25] : [0] });
    io.observe(el);
    st.io = io;
  }

  function start(st) {
    if (st.started) return;
    st.started = true;
    if (st.io) { st.io.disconnect(); st.io = null; }
    var el = st.el, cfg = st.cfg;
    el.classList.add('is-loading');
    loadLib(function () {
      if (st.mv) return;
      var mv = document.createElement('model-viewer');
      mv.setAttribute('src', cfg.src);
      mv.setAttribute('alt', cfg.title);
      mv.setAttribute('loading', 'eager');
      mv.setAttribute('reveal', 'auto');
      if (cfg.interact) { mv.setAttribute('camera-controls', ''); mv.setAttribute('touch-action', 'pan-y'); }
      else { mv.setAttribute('disable-zoom', ''); mv.setAttribute('disable-pan', ''); mv.setAttribute('disable-tap', ''); }
      mv.setAttribute('interaction-prompt', 'none');
      mv.setAttribute('camera-orbit', cfg.orbit);
      mv.setAttribute('max-camera-orbit', 'auto 88deg auto');
      mv.setAttribute('shadow-intensity', '1');
      mv.setAttribute('shadow-softness', '0.8');
      mv.setAttribute('exposure', '1.05');
      mv.setAttribute('tone-mapping', 'neutral');
      if (cfg.sky) mv.setAttribute('environment-image', cfg.sky);
      if (cfg.rotate) { mv.setAttribute('auto-rotate', ''); mv.setAttribute('rotation-per-second', cfg.speed); mv.setAttribute('auto-rotate-delay', String(cfg.delay)); }
      if (cfg.ar) {
        mv.setAttribute('ar', '');
        mv.setAttribute('ar-modes', 'webxr quick-look');   // same as yourgreenhouses.design: Scene Viewer ignores the miniature scale
        mv.setAttribute('ar-scale', 'fixed');
        mv.setAttribute('ar-placement', 'floor');
        if (cfg.usdz) mv.setAttribute('ios-src', cfg.usdz);
        var arSlot = document.createElement('span'); arSlot.setAttribute('slot', 'ar-button'); arSlot.hidden = true; mv.appendChild(arSlot);
        mv.insertAdjacentHTML('beforeend', xrHtml());
      }
      el.insertBefore(mv, el.firstChild);
      st.mv = mv;
      if (cfg.ar) wireXR(st);

      var bar = el.querySelector('.ygh3d__progress i');
      mv.addEventListener('progress', function (ev) { if (bar) bar.style.width = Math.round((ev.detail && ev.detail.totalProgress || 0) * 100) + '%'; });
      mv.addEventListener('load', function () {
        el.classList.remove('is-loading');
        el.classList.add('is-loaded');
        setTimeout(function () {
          var p = el.querySelector('.ygh3d__poster'); if (p) p.remove();
          var pr = el.querySelector('.ygh3d__progress'); if (pr) pr.remove();
        }, 500);
        var arBtn = el.querySelector('[data-ygh3d-act="ar"]');
        if (arBtn && mv.canActivateAR) arBtn.hidden = false;
        if (st.arWait) { var w = st.arWait; st.arWait = null; try { w(); } catch (e) {} }
        track(cfg.key, st.warm ? 'preload' : 'load');   // preload = warmed in the background (spec drawer), not opened
        watchTime(st);
      });
      mv.addEventListener('error', function () {
        el.classList.add('is-error');
        var l = el.querySelector('.ygh3d__launch'); if (l) l.innerHTML = '<span>Could not load the 3D model</span>';
        var sp = el.querySelector('.ygh3d__spin'); if (sp) sp.outerHTML = '<span class="ygh3d__err">3D model unavailable</span>';
      });
      st.touched = false;
      mv.addEventListener('camera-change', function (ev) {
        if (st.touched || !ev.detail || ev.detail.source !== 'user-interaction') return;
        st.touched = true;
        el.classList.add('is-touched');
        if (cfg.rotate) mv.setAttribute('auto-rotate-delay', '3000');   // resume the slow spin a moment after they let go
        if (!st.tracked) { st.tracked = true; track(cfg.key, 'interact'); }
      });
    });
  }

  function act(st, a) {
    var mv = st.mv, cfg = st.cfg;
    if (a === 'full') { track(cfg.key, 'expand'); openFull(cfg); return; }
    if (a === 'ar' && mv) { track(cfg.key, 'ar'); try { mv.activateAR(); } catch (e) {} }
  }

  // ---- second size (Arrow 8'): the hero announces the selection, every viewer with an alt model follows ----
  function useAltSize(cfg, sel) {
    if (!sel) return false;
    var vals = [sel.length, sel.width];
    for (var i = 0; i < vals.length; i++) { var v = vals[i]; if (v && String(v).replace(/[\u2032\u2019]/g, "'").indexOf(cfg.altMatch) === 0) return true; }
    return false;
  }
  document.addEventListener('npwmp:selection', function (e) {
    [].forEach.call(document.querySelectorAll('[data-ygh3d-alt-src]'), function (el) {
      var st = el.__ygh3d; if (!st) return;   // not mounted yet: mount() reads window.__npwmpSel
      var cfg = st.cfg, alt = useAltSize(cfg, e.detail);
      var src = alt ? cfg.altSrc : cfg.mainSrc, usdz = alt ? (cfg.altUsdz || '') : cfg.mainUsdz;
      if (src === cfg.src) return;
      cfg.src = src; cfg.usdz = usdz;
      var mv = st.mv; if (!mv) return;
      mv.setAttribute('src', src);
      if (st.arMode !== 'mini') { if (usdz) mv.setAttribute('ios-src', usdz); else mv.removeAttribute('ios-src'); }
      track(cfg.key, alt ? 'size_alt' : 'size_main');
    });
  });

  // ---- AR from outside the stage (section button + picker), same two sizes as yourgreenhouses.design/ar.html ----
  var MINI = 0.125;   // miniature = 1:8, a 17 ft greenhouse becomes about 26 in on a table
  function arMode(st, m) {
    var mv = st.mv; if (!mv) return;
    st.arMode = m;
    var box = mv.querySelector('.ygh3d-xr');
    if (box) {
      [].forEach.call(box.querySelectorAll('[data-xr-mode]'), function (b) { b.classList.toggle('on', b.getAttribute('data-xr-mode') === m); });
      var h = box.querySelector('.ygh3d-xr__hint'); if (h) h.textContent = XR_HINT[m] || XR_HINT.life;
    }
    var put = function (k, v) { if (v == null) { if (mv.hasAttribute(k)) mv.removeAttribute(k); } else if (mv.getAttribute(k) !== v) mv.setAttribute(k, v); };
    if (m === 'mini') { put('scale', MINI + ' ' + MINI + ' ' + MINI); put('ar-scale', 'auto'); put('ios-src', null); }   // iOS: model-viewer writes a USDZ on the fly, so the scale carries over
    else { put('scale', '1 1 1'); put('ar-scale', 'fixed'); if (st.cfg.usdz) put('ios-src', st.cfg.usdz); }
    setTimeout(function () { try { if (mv.updateFraming) mv.updateFraming(); } catch (e) {} }, 60);
  }
  // ---- in-AR controls (Android WebXR): hints that follow the session, Life-size / Miniature switch, Start over, close ----
  var XR_HINT = { life: 'Point your phone at the ground and move it slowly', mini: 'Point at a table or the floor, then tap to place it' };
  function xrHtml() {
    return '<div class="ygh3d-xr" hidden>' +
      '<p class="ygh3d-xr__hint">' + XR_HINT.life + '</p>' +
      '<div class="ygh3d-xr__seg" role="group" aria-label="Size"><button type="button" data-xr-mode="life" class="on">Life-size</button><button type="button" data-xr-mode="mini">Miniature</button></div>' +
      '<button type="button" class="ygh3d-xr__btn" data-xr-reset>Start over</button>' +
      '</div>' +
      '<button slot="exit-webxr-ar-button" type="button" class="ygh3d-xr-x" aria-label="Close">' + ICON.close + '</button>';
  }
  function restartAR(st) {
    st.again = true; clearTimeout(st.againT);
    st.againT = setTimeout(function () { st.again = false; }, 4000);
    if (xrSession) { try { xrSession.end(); } catch (e) {} }
  }
  function wireXR(st) {
    var mv = st.mv, box = mv && mv.querySelector('.ygh3d-xr'); if (!box) return;
    var hint = box.querySelector('.ygh3d-xr__hint'), hintT = null, hideT = null;
    st.arMode = st.arMode || 'life';
    function say(t, delay) {
      clearTimeout(hintT); clearTimeout(hideT);
      hintT = setTimeout(function () { hint.innerHTML = t; hint.classList.remove('off'); hideT = setTimeout(function () { hint.classList.add('off'); }, st.arMode === 'mini' ? 15000 : 30000); }, delay || 0);
    }
    // taps on the controls must not place or move the model
    box.addEventListener('beforexrselect', function (e) { e.preventDefault(); });
    box.addEventListener('pointerdown', function (e) { e.stopPropagation(); });
    box.addEventListener('click', function (e) {
      var b = e.target.closest('[data-xr-mode],[data-xr-reset]'); if (!b) return;
      e.preventDefault(); e.stopPropagation();
      var m = b.getAttribute('data-xr-mode');
      if (m) { if (m === st.arMode) return; arMode(st, m); track(st.cfg.key, 'ar_switch_' + m); }
      else track(st.cfg.key, 'ar_restart');
      restartAR(st);   // a fresh start places the new size near you instead of shrinking it far away
    });
    mv.addEventListener('ar-status', function (e) {
      var s = e.detail && e.detail.status;
      box.hidden = !(s === 'session-started' || s === 'object-placed');
      if (s === 'session-started') say(st.arMode === 'mini' ? 'Point at a table or the floor, then tap to place it' : 'Point your phone at the ground and move it slowly, then tap to place it');
      else if (s === 'object-placed') { say(st.arMode === 'mini' ? 'Drag to move it, two fingers to turn or resize.' : 'Drag to move it, two fingers to turn.<br>Try walking inside.', 1200); track(st.cfg.key, 'ar_placed'); }
      else if (s === 'failed') track(st.cfg.key, 'ar_failed');
      else if (s === 'not-presenting' && st.again) { st.again = false; clearTimeout(st.againT); setTimeout(function () { if (mv.canActivateAR) mv.activateAR(); }, 350); }
    });
  }

  // 'ready' | 'loading' (onReady fires once the model is in) | 'no-ar' (this browser cannot place it)
  function arState(el, onReady) {
    var st = el && (el.__ygh3d || mount(el)); if (!st) return 'no-ar';
    if (!st.started) start(st);
    var mv = st.mv;
    if (!mv || !mv.loaded) { st.arWait = onReady || null; return 'loading'; }
    return mv.canActivateAR ? 'ready' : 'no-ar';
  }
  function arGo(el, mode) {
    var st = el && el.__ygh3d, mv = st && st.mv;
    if (!mv || !mv.loaded || !mv.canActivateAR) return false;
    arMode(st, mode === 'mini' ? 'mini' : 'life');
    track(st.cfg.key, 'ar_' + (mode === 'mini' ? 'mini' : 'life'));
    try { mv.activateAR(); } catch (e) { return false; }
    return true;
  }

  // ---- full-screen viewer (opened from a section button; model + hint + AR + close) ----
  var fullEl = null;
  function closeFull() {
    if (!fullEl) return;
    fullEl.remove(); fullEl = null;
    document.documentElement.classList.remove('ygh3d-lock');
    document.removeEventListener('keydown', onKey);
  }
  function onKey(e) { if (e.key === 'Escape') closeFull(); }
  function openFull(cfg) {
    closeFull();
    var ov = document.createElement('div');
    ov.className = 'ygh3d-full';
    ov.setAttribute('role', 'dialog');
    ov.setAttribute('aria-label', cfg.title + ' in 3D');
    ov.innerHTML = '<div class="ygh3d-full__head"><span class="ygh3d-full__title">' + ICON.cube + '<span>' + esc(cfg.title) + '</span><em>3D model</em></span>' +
      '<button type="button" class="ygh3d-full__close" aria-label="Close">' + ICON.close + '</button></div>' +
      '<div class="ygh3d-full__stage"></div>';
    var stage = ov.querySelector('.ygh3d-full__stage');
    stage.setAttribute('data-ygh3d', '');
    stage.setAttribute('data-ygh3d-src', cfg.src);
    if (cfg.usdz) stage.setAttribute('data-ygh3d-usdz', cfg.usdz);
    if (cfg.poster) stage.setAttribute('data-ygh3d-poster', cfg.poster);
    if (cfg.sky) stage.setAttribute('data-ygh3d-sky', cfg.sky);
    stage.setAttribute('data-ygh3d-mode', 'auto');
    stage.setAttribute('data-ygh3d-ar', '1');
    stage.setAttribute('data-ygh3d-rotate', cfg.rotate ? '1' : '0');
    stage.setAttribute('data-ygh3d-orbit', cfg.orbit.replace(/\s(auto|[\d.]+%)\s*$/, ' 85%'));   // a little closer than the fitted view
    stage.setAttribute('data-ygh3d-title', cfg.title);
    stage.setAttribute('data-ygh3d-key', 'full');
    document.body.appendChild(ov);
    fullEl = ov;
    document.documentElement.classList.add('ygh3d-lock');
    ov.querySelector('.ygh3d-full__close').addEventListener('click', closeFull);
    document.addEventListener('keydown', onKey);
    var st = mount(stage);
    if (st) start(st);
    return ov;
  }

  // ---- spec-drawer views: 3D model | floor plan | elevations ----
  function planGalleryOf(id) { return id ? document.getElementById(id) : null; }
  // back to the opening view: starting angle and distance, centred, spinning, hint showing (used when a drawer view returns to the model)
  function resetView(el) {
    var st = el && el.__ygh3d, mv = st && st.mv; if (!mv) return;
    var cfg = st.cfg;
    try {
      mv.setAttribute('camera-orbit', cfg.orbit);
      mv.setAttribute('camera-target', 'auto auto auto');
      mv.setAttribute('field-of-view', 'auto');
      if (mv.jumpCameraToGoal) mv.jumpCameraToGoal();
      if (mv.resetTurntableRotation) mv.resetTurntableRotation(0);
      if (cfg.rotate) mv.setAttribute('auto-rotate-delay', String(cfg.delay));
    } catch (e) {}
    st.touched = false;
    el.classList.remove('is-touched');
  }
  function setTab(gal, id) {
    if (!gal) return;
    var is3d = String(id) === '3d';
    var wasPlan = !gal.classList.contains('is-3d');
    var slide3d = gal.querySelector('.plan-slide--3d');
    var imgs = gal.querySelectorAll('[data-npwmp-plan-slide]');
    var counter = gal.querySelector('[data-npwmp-plan-i]');
    gal.classList.toggle('is-3d', is3d);
    if (slide3d) slide3d.classList.toggle('is-active', is3d);
    if (!is3d) {
      var n = parseInt(id, 10) || 0;
      for (var i = 0; i < imgs.length; i++) imgs[i].classList.toggle('is-active', i === n);
      if (counter) counter.textContent = n + 1;
    }
    document.querySelectorAll('[data-ygh3d-tabfor="' + gal.id + '"]').forEach(function (t) {
      t.classList.toggle('is-active', t.getAttribute('data-ygh3d-tab') === String(id));
    });
    if (is3d) {
      var s = slide3d && slide3d.querySelector('[data-ygh3d]'); var st = s && (s.__ygh3d || mount(s));
      if (st && !st.started && panelOpen(gal) && shown(slide3d)) start(st);
      else if (st && wasPlan) resetView(s);
    }
  }
  // The model in the desktop plan panel loaded on every page view, phones included (30 Oct: 853 of 1,046 phone views): the setup picks
  // the 3D view while the page boots, when the drawer markup still sits in the page for a moment before the hero moves it into its
  // hidden drawer host. So the setup never starts it: the panel opening does (really on the page: laid out, not visibility:hidden), or the
  // background warm-up (warmDrawer) once the page has settled.
  function panelOpen(gal) { return gal.classList.contains('is-visible') || gal.classList.contains('is-fullscreen'); }
  function shown(el) { return !!el && el.getClientRects().length > 0 && getComputedStyle(el).visibility !== 'hidden'; }
  // the desktop panel opening (its class changes, then the drawer host turns visible): start the model if the 3D view is the one showing
  function wake3d(gal) {
    if (!gal.classList.contains('is-3d') || !panelOpen(gal)) return;
    var slide3d = gal.querySelector('.plan-slide--3d'), s = slide3d && slide3d.querySelector('[data-ygh3d]'), st = s && (s.__ygh3d || mount(s));
    if (st && !st.started && shown(slide3d)) start(st);
  }
  function syncTabsFromImages(gal) {
    var imgs = gal.querySelectorAll('[data-npwmp-plan-slide]');
    for (var i = 0; i < imgs.length; i++) if (imgs[i].classList.contains('is-active')) { setTab(gal, i); return; }
  }
  // phone / tablet card in the drawer body: the model, or one drawing (square, tap = the zoomable full-screen view)
  function setCard(card, id) {
    if (!card) return;
    id = String(id);
    var plan = id !== '3d';
    var wasPlan = card.classList.contains('is-plan');
    card.classList.toggle('is-plan', plan);
    [].forEach.call(card.querySelectorAll('[data-card-plan]'), function (p) { p.classList.toggle('is-active', p.getAttribute('data-card-plan') === id); });
    if (!plan) { var s = card.querySelector('[data-ygh3d]'); var st = s && (s.__ygh3d || mount(s)); if (st && !st.started) start(st); else if (st && wasPlan) resetView(s); }
    track('drawer-m', 'view_' + (plan ? 'plan' + id : '3d'));
  }
  // Spec drawer: its model warms up in the background once the page has settled (Rai 10-30), so it is already there when Specifications
  // opens while the shopper is still picking add-ons. Load event + 2.5 s + an idle moment, so the hero always comes first; only the viewer
  // this device will show (the plan panel from 1201 px, the card at the top of the drawer below that); skipped with Save-Data or on 2G.
  var warmQueued = false;
  function warmDrawer() {
    var c = navigator.connection || {}; if (c.saveData || /(^|-)2g$/.test(c.effectiveType || '')) return;
    var desk = window.matchMedia && window.matchMedia('(min-width: 1201px)').matches;
    var el = document.querySelector(desk ? '[data-ygh3d-tabs] .plan-slide--3d [data-ygh3d]' : '[data-spec-card] [data-ygh3d]');
    var st = el && (el.__ygh3d || mount(el));
    if (st && !st.started) { st.warm = true; start(st); }
  }
  function afterSettle(fn) {
    var go = function () { setTimeout(function () { if ('requestIdleCallback' in window) requestIdleCallback(fn, { timeout: 4000 }); else fn(); }, 2500); };
    if (document.readyState === 'complete') go(); else window.addEventListener('load', go, { once: true });
  }
  function initGallery(gal) {
    if (gal.__ygh3dTabs) return;
    gal.__ygh3dTabs = true;
    if (!warmQueued) { warmQueued = true; afterSettle(warmDrawer); }
    setTab(gal, '3d');
    if ('MutationObserver' in window) new MutationObserver(function () { wake3d(gal); setTimeout(function () { wake3d(gal); }, 450); }).observe(gal, { attributes: true, attributeFilter: ['class'] });
    // the hero's own prev / next cycle the images; leaving the 3D view on the way
    ['[data-npwmp-plan-prev]', '[data-npwmp-plan-next]'].forEach(function (sel, k) {
      var b = gal.querySelector(sel);
      if (!b) return;
      b.addEventListener('click', function () {
        if (gal.classList.contains('is-3d')) {
          var imgs = gal.querySelectorAll('[data-npwmp-plan-slide]');
          setTab(gal, k === 0 ? Math.max(0, imgs.length - 1) : 0);
        }
        setTimeout(function () { syncTabsFromImages(gal); }, 0);
      }, true);
    });
  }
  // Open the Specifications drawer on the floor plan (used by the 3D section's "Floor plan" button).
  function openPlans(planIndex) {
    var row = document.querySelector('[data-link-target="specs"] .acc-row');
    if (!row) { var t = document.getElementById('specs'); if (t) t.scrollIntoView({ behavior: 'smooth' }); return false; }
    row.click();
    setTimeout(function () {
      var gal = document.querySelector('[data-ygh3d-tabs]');
      if (gal && !gal.querySelector('[data-npwmp-plan-slide]')) planIndex = '3d';   // no drawings for this product: open on the model
      if (gal) setTab(gal, planIndex || 0);
      if (!window.matchMedia('(min-width: 1201px)').matches) {   // phones + tablets: the drawing opens in the card at the top of the drawer
        var card = gal && document.querySelector('[data-spec-card][data-plans="' + gal.id + '"]');
        if (card) setCard(card, planIndex || 0);
        else { var open = document.querySelector('.acc-drawer.is-open [data-npwmp-plans-open], [data-npwmp-plans-open]'); if (open) open.click(); }
      }
    }, 380);
    return true;
  }
  document.addEventListener('click', function (e) {
    var t = e.target.closest && e.target.closest('[data-ygh3d-tab]');
    if (t) {
      var gal = planGalleryOf(t.getAttribute('data-ygh3d-tabfor'));
      if (gal) { e.preventDefault(); setTab(gal, t.getAttribute('data-ygh3d-tab')); }
      var card = document.querySelector('[data-spec-card][data-plans="' + t.getAttribute('data-ygh3d-tabfor') + '"]');
      if (card && card.offsetParent !== null) setCard(card, t.getAttribute('data-ygh3d-tab'));
      return;
    }
    var cp = e.target.closest && e.target.closest('[data-card-plan]');
    if (cp) {   // drawing in the card: open the hero's zoomable full-screen plan view on the same drawing
      var cg = planGalleryOf(cp.closest('[data-spec-card]').getAttribute('data-plans'));
      if (cg) { setTab(cg, cp.getAttribute('data-card-plan')); cg.classList.add('is-fullscreen'); track('drawer-m', 'plan_fullscreen'); }
      return;
    }
    var o = e.target.closest && e.target.closest('[data-npwmp-plans-open]');
    if (o) { // the phone / tablet pop-up shows the drawings; the 3D card sits above it already
      var host = o.closest('.acc-drawer');
      var id = host && host.getAttribute('data-ygh3d-plans');
      var g = planGalleryOf(id);
      if (g && g.classList.contains('is-3d')) setTab(g, 0);
    }
    var c = e.target.closest && e.target.closest('[data-ygh3d-corner]');
    if (c) {
      var s = c.querySelector('[data-ygh3d]');
      var st = s && (s.__ygh3d || mount(s));
      if (!st) return;
      track(st.cfg.key, 'expand');
      var ev = new CustomEvent('ygh3d:corner', { bubbles: true, cancelable: true, detail: st.cfg });
      if (c.dispatchEvent(ev)) openFull(st.cfg);   // nobody took it (no gallery around): full screen
    }
  });

  // Corner tile with a clip: fetch the viewer library on the first hover / touch so the tap feels instant.
  ['pointerenter', 'touchstart'].forEach(function (evn) {
    document.addEventListener(evn, function (e) { if (e.target.closest && e.target.closest('[data-ygh3d-corner]')) loadLib(); }, { capture: true, passive: true });
  });

  // ---- scan now + whenever new stages appear (gallery slides are built by JS) ----
  function scan(root) {
    root = root || document;
    var list = root.querySelectorAll ? root.querySelectorAll('[data-ygh3d]') : [];
    for (var i = 0; i < list.length; i++) mount(list[i]);
    if (root.matches && root.matches('[data-ygh3d]')) mount(root);
    var gals = root.querySelectorAll ? root.querySelectorAll('[data-ygh3d-tabs]') : [];
    for (var j = 0; j < gals.length; j++) initGallery(gals[j]);
  }
  function boot() {
    scan(document);
    if ('MutationObserver' in window) {
      new MutationObserver(function (muts) {
        for (var i = 0; i < muts.length; i++) {
          var added = muts[i].addedNodes;
          for (var k = 0; k < added.length; k++) if (added[k].nodeType === 1) scan(added[k]);
        }
      }).observe(document.body, { childList: true, subtree: true });
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();

  window.ygh3d = { mount: mount, scan: scan, start: start, openFull: openFull, closeFull: closeFull, setTab: setTab, openPlans: openPlans, loadLib: loadLib, arState: arState, arGo: arGo };
})();
